import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, ChevronLeft, ChevronRight, User, X, Minus, Settings, Eye, EyeOff } from 'lucide-react';
import userService from '../services/userService';

const AdminUsersPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Users';
  }, []);

  // State for real user data
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState('');
  const [filterByRole, setFilterByRole] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Collapsible sections state
  const [expandedUser, setExpandedUser] = useState(null);
  
  // Edit functionality state
  const [editingSection, setEditingSection] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [userPassword, setUserPassword] = useState('');
  const [originalPassword, setOriginalPassword] = useState(''); // Store decrypted password when editing

  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addUserData, setAddUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    user_address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    phone_number: '',
    password: '',
    is_active: true,
    is_superuser: false,
    is_doctor: false,
    is_patient: false,
    is_clinician: false,
    patient_type: 'Standard'
  });
  const [addLoading, setAddLoading] = useState(false);
  
  // Toast notifications state
  const [toasts, setToasts] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Dropdown states
  const [showIncludeInactiveDropdown, setShowIncludeInactiveDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for dropdowns
  const includeInactiveRef = useRef(null);
  const roleRef = useRef(null);
  const sortRef = useRef(null);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No authentication token found');
        setError('Please log in to view users. No authentication token found.');
        return;
      }

      console.log('👥 Attempting to fetch users via userService...');
      const data = await userService.getAllUsers();
      
      if (!data || data.length === 0) {
        console.warn('⚠️ No users found');
        setUsersData([]);
        return;
      }
      
      setUsersData(data);
      console.log('✅ Successfully fetched users via userService');
      
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setError(`Failed to load users: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Helper function to get user role
  const getUserRole = (user) => {
    if (user.is_superuser) return 'Superuser';
    if (user.is_doctor) return 'Doctor';
    if (user.is_clinician) return 'Clinician';
    if (user.is_patient) return 'Patient';
    return 'User';
  };

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    return usersData.filter(user => {
      const matchesSearch = searchTerm === '' || 
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getUserRole(user).toLowerCase().includes(searchTerm.toLowerCase());

      const matchesActive = includeInactive === '' || 
        (includeInactive === 'Yes' && user.is_active) ||
        (includeInactive === 'No' && !user.is_active);

      const matchesRole = filterByRole === '' || getUserRole(user) === filterByRole;

      return matchesSearch && matchesActive && matchesRole;
    }).sort((a, b) => {
      switch (sortBy) {
        case 'Name (A-Z)':
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        case 'Name (Z-A)':
          return `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`);
        case 'Role':
          return getUserRole(a).localeCompare(getUserRole(b));
        case 'Date Joined':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'Status':
          return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
        default:
          return 0;
      }
    });
  }, [usersData, searchTerm, includeInactive, filterByRole, sortBy]);

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Toast notification functions
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Collapsible functions
  const toggleUserExpansion = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
    // Reset edit state when collapsing
    if (expandedUser === userId) {
      setEditingSection(null);
      setEditingUserId(null);
      setEditFormData({});
    }
  };

  // Edit functionality
  const startEdit = async (section, userId) => {
    const user = usersData.find(u => u.id === userId);
    if (!user) return;

    setEditingSection(section);
    setEditingUserId(userId);
    
    // If editing security settings, fetch current password in hidden format
    if (section === 'security-settings') {
      try {
        // Fetch user data with password for editing
        const userData = await userService.getUserById(userId, true);
        const currentPassword = userData.password || '••••••••••••'; // Current password in hidden format
        setOriginalPassword(currentPassword); // Store original for comparison
        setUserPassword(currentPassword);
        setEditFormData(prepareEditData(user, section, currentPassword));
      } catch (error) {
        console.error('Error fetching user password:', error);
        // Use placeholder if API call fails
        const placeholder = '••••••••••••';
        setOriginalPassword(placeholder);
        setUserPassword(placeholder);
        setEditFormData(prepareEditData(user, section, placeholder));
      }
    } else {
      setEditFormData(prepareEditData(user, section));
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditingUserId(null);
    setEditFormData({});
    setUserPassword('');
    setOriginalPassword('');
  };

  const prepareEditData = (user, section, password = '') => {
    switch (section) {
      case 'user-details':
        return {
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          user_address: user.user_address || '',
          suburb: user.suburb || '',
          state: user.state || '',
          postcode: user.postcode || '',
          country: user.country || '',
          phone_number: user.phone_number || '',
          email: user.email || '',
          is_active: user.is_active || false
        };
      case 'security-settings':
        return {
          password: password, // Use actual password when provided
          is_superuser: user.is_superuser || false,
          is_doctor: user.is_doctor || false,
          is_patient: user.is_patient || false,
          is_clinician: user.is_clinician || false
        };
      default:
        return {};
    }
  };

  // Reset password function
  const resetPassword = () => {
    setUserPassword('');
    updateEditFormData('password', '');
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveChanges = async () => {
    if (!editingUserId || !editingSection) return;

    try {
      setSaveLoading(true);
      
      const dataToSave = { ...editFormData };
      
      // Only update password if it was actually changed
      if (editingSection === 'security-settings') {
        if (dataToSave.password === originalPassword || !dataToSave.password || dataToSave.password.trim() === '') {
          // Password wasn't changed or was cleared (reset), remove it from update data
          delete dataToSave.password;
          console.log('Password unchanged or cleared, not updating password');
        } else {
          // Password was changed to a new value, include it in update
          console.log('Password was changed to new value, updating password');
        }
      }
      
      const updatedUser = await userService.updateUser(editingUserId, dataToSave);
      
      // Update local state
      setUsersData(prevUsers => 
        prevUsers.map(user => 
          user.id === editingUserId ? { ...user, ...updatedUser } : user
        )
      );
      
      // Cancel edit mode
      cancelEdit();
      
      // Show success message
      const sectionName = editingSection === 'user-details' ? 'User details' : 'Security settings';
      addToast(`${sectionName} updated successfully!`, 'success');
      
    } catch (error) {
      console.error('Error updating user:', error);
      addToast(`Failed to update user: ${error.message}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle dropdown toggles
  const toggleIncludeInactiveDropdown = () => {
    setShowIncludeInactiveDropdown(!showIncludeInactiveDropdown);
    setShowRoleDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleRoleDropdown = () => {
    setShowRoleDropdown(!showRoleDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowSortDropdown(false);
  };

  const toggleSortDropdown = () => {
    setShowSortDropdown(!showSortDropdown);
    setShowIncludeInactiveDropdown(false);
    setShowRoleDropdown(false);
  };

  // Handle dropdown selections
  const handleIncludeInactiveSelect = (option) => {
    setIncludeInactive(option);
    setShowIncludeInactiveDropdown(false);
  };

  const handleRoleSelect = (role) => {
    setFilterByRole(role);
    setShowRoleDropdown(false);
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };

  // Reset filters functionality
  const resetFilters = () => {
    setSearchTerm('');
    setIncludeInactive('');
    setFilterByRole('');
    setSortBy('');
    setCurrentPage(1);
  };

  // Add user modal functions
  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddUserData({
      first_name: '',
      last_name: '',
      email: '',
      user_address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: '',
      phone_number: '',
      password: '',
      is_active: true,
      is_superuser: false,
      is_doctor: false,
      is_patient: false,
      is_clinician: false
    });
  };

  const updateAddUserData = (field, value) => {
    setAddUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddUser = async () => {
    try {
      setAddLoading(true);

      const newUser = await userService.createUser(addUserData);
      
      // Add to local state
      setUsersData(prevUsers => [...prevUsers, newUser]);
      
      // Close modal
      closeAddModal();
      
      // Show success message
      addToast('User created successfully!', 'success');
      
    } catch (error) {
      console.error('Error creating user:', error);
      addToast(`Failed to create user: ${error.message}`, 'error');
    } finally {
      setAddLoading(false);
    }
  };

  // Handle user actions
  const handleUserAction = (userId) => {
    toggleUserExpansion(userId);
  };

  // Handle pagination
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (includeInactiveRef.current && !includeInactiveRef.current.contains(event.target)) {
        setShowIncludeInactiveDropdown(false);
      }
      if (roleRef.current && !roleRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique roles from users data
  const uniqueRoles = useMemo(() => {
    const roles = [...new Set(usersData.map(user => getUserRole(user)))];
    return roles.sort();
  }, [usersData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchUsers}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue header */}
      <div className="bg-blue-600 text-white py-1 px-2">
        <h1 className="text-xl font-bold text-center">Users</h1>
      </div>

      <div className="flex">
        {/* Left Column - Filters (25%) */}
        <div className="w-1/4 bg-gray-50 min-h-screen p-4">
          <div className="bg-white rounded-lg border border-blue-200 p-3 mt-3">
            <div className="space-y-3">
              {/* Add User Button */}
              <div>
                <button
                  onClick={openAddModal}
                  className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add User
                </button>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Include Inactive */}
              <div className="relative" ref={includeInactiveRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Include Inactive:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleIncludeInactiveDropdown}
                >
                  <span className={includeInactive ? 'text-gray-900' : 'text-gray-500'}>
                    {includeInactive || 'Yes / No'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showIncludeInactiveDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Yes', 'No'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleIncludeInactiveSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter By Role */}
              <div className="relative" ref={roleRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Filter By Role:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleRoleDropdown}
                >
                  <span className={filterByRole ? 'text-gray-900' : 'text-gray-500'}>
                    {filterByRole || 'Please Select'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showRoleDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-32 overflow-y-auto">
                    {uniqueRoles.map((role) => (
                      <div
                        key={role}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleRoleSelect(role)}
                      >
                        {role}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort By */}
              <div className="relative" ref={sortRef}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Sort By:
                </label>
                <div 
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white cursor-pointer flex justify-between items-center hover:bg-gray-50"
                  onClick={toggleSortDropdown}
                >
                  <span className={sortBy ? 'text-gray-900' : 'text-gray-500'}>
                    {sortBy || 'Sort Options'}
                  </span>
                  <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {showSortDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
                    {['Name (A-Z)', 'Name (Z-A)', 'Role', 'Date Joined', 'Status'].map((option) => (
                      <div
                        key={option}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSortSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Filters Button */}
              <div className="pt-2">
                <button
                  onClick={resetFilters}
                  className="w-full px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-colors flex items-center justify-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Reset Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - User List (75%) */}
        <div className="w-3/4 bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-4">
              <div className="space-y-1">
                {currentUsers.map((user) => (
                  <div key={user.id} className="border border-gray-200 rounded-md">
                    {/* User Header */}
                    <div className="flex justify-between items-center p-3 hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <div>
                      <span className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              <span className="mr-3">{user.email}</span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                user.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {getUserRole(user)}
                      </span>
                            </div>
                          </div>
                        </div>
                    </div>
                    <button
                      onClick={() => handleUserAction(user.id)}
                      className="w-7 h-7 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                        {expandedUser === user.id ? (
                          <Minus className="h-3 w-3" />
                        ) : (
                      <Plus className="h-3 w-3" />
                        )}
                    </button>
                    </div>

                    {/* Collapsible Content */}
                    {expandedUser === user.id && (
                      <div className="border-t border-gray-200 bg-gray-50 space-y-3 p-4">
                        
                        {/* User Details Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`flex justify-between items-center px-3 py-1.5 rounded-t-lg text-white ${
                            'bg-gradient-to-r from-blue-500 to-blue-600'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <User className="h-3 w-3 mr-2" />
                              User Details
                            </h3>
                            {editingSection === 'user-details' && editingUserId === user.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('user-details', user.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            {/* First Row */}
                            <div className="grid grid-cols-5 gap-2 mb-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <div className="flex gap-1">
                                    <input
                                      type="text"
                                      value={editFormData.first_name || ''}
                                      onChange={(e) => updateEditFormData('first_name', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      placeholder="First Name"
                                    />
                                    <input
                                      type="text"
                                      value={editFormData.last_name || ''}
                                      onChange={(e) => updateEditFormData('last_name', e.target.value)}
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      placeholder="Last Name"
                                    />
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.user_address || ''}
                                    onChange={(e) => updateEditFormData('user_address', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Address"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.user_address || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.suburb || ''}
                                    onChange={(e) => updateEditFormData('suburb', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Suburb"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.suburb || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.state || ''}
                                    onChange={(e) => updateEditFormData('state', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="State"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.state || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.postcode || ''}
                                    onChange={(e) => updateEditFormData('postcode', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Postcode"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.postcode || '-'}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Second Row */}
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.country || ''}
                                    onChange={(e) => updateEditFormData('country', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Country"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.country || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="text"
                                    value={editFormData.phone_number || ''}
                                    onChange={(e) => updateEditFormData('phone_number', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Phone Number"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.phone_number || '-'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                                {editingSection === 'user-details' && editingUserId === user.id ? (
                                  <input
                                    type="email"
                                    value={editFormData.email || ''}
                                    onChange={(e) => updateEditFormData('email', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Email"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    {user.email || '-'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Security Settings Section */}
                        <div className="bg-white rounded-lg border border-gray-200">
                          <div className={`flex justify-between items-center px-3 py-1.5 rounded-t-lg text-white ${
                            'bg-gradient-to-r from-blue-500 to-blue-600'
                          }`}>
                            <h3 className="text-xs font-medium flex items-center">
                              <Settings className="h-3 w-3 mr-2" />
                              Security Settings
                            </h3>
                            {editingSection === 'security-settings' && editingUserId === user.id ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={saveChanges}
                                  disabled={saveLoading}
                                  className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                >
                                  {saveLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => startEdit('security-settings', user.id)}
                                className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                              >
                                Edit Record
                              </button>
                            )}
                          </div>
                          <div className="p-3">
                            {/* Password Section */}
                            <div className="mb-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                              {editingSection === 'security-settings' && editingUserId === user.id ? (
                                <div className="flex gap-1">
                                  <input
                                    type="password"
                                    value={editFormData.password || ''}
                                    onChange={(e) => {
                                      updateEditFormData('password', e.target.value);
                                    }}
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Leave unchanged or enter new password"
                                  />
                                  <button 
                                    onClick={resetPassword}
                                    type="button"
                                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                  >
                                    Clear
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <div className="flex-1 text-xs text-gray-900 border border-gray-200 rounded px-2 py-1 bg-gray-50">
                                    ••••••••
                                  </div>
                                  <button 
                                    onClick={() => startEdit('security-settings', user.id)}
                                    type="button"
                                    className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                  >
                                    Reset
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* User Roles Section */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">User Roles</label>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="flex items-center">
                                  {editingSection === 'security-settings' && editingUserId === user.id ? (
                                    <input
                                      type="checkbox"
                                      id={`superuser-${user.id}`}
                                      checked={editFormData.is_superuser || false}
                                      onChange={(e) => updateEditFormData('is_superuser', e.target.checked)}
                                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                  ) : (
                                    <div className={`h-3 w-3 rounded border-2 ${user.is_superuser ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {user.is_superuser && <div className="text-white text-xs">✓</div>}
                                    </div>
                                  )}
                                  <label htmlFor={`superuser-${user.id}`} className="ml-2 block text-xs text-gray-900">
                                    Superuser
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  {editingSection === 'security-settings' && editingUserId === user.id ? (
                                    <input
                                      type="checkbox"
                                      id={`doctor-${user.id}`}
                                      checked={editFormData.is_doctor || false}
                                      onChange={(e) => updateEditFormData('is_doctor', e.target.checked)}
                                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                  ) : (
                                    <div className={`h-3 w-3 rounded border-2 ${user.is_doctor ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {user.is_doctor && <div className="text-white text-xs">✓</div>}
                                    </div>
                                  )}
                                  <label htmlFor={`doctor-${user.id}`} className="ml-2 block text-xs text-gray-900">
                                    Doctor
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  {editingSection === 'security-settings' && editingUserId === user.id ? (
                                    <input
                                      type="checkbox"
                                      id={`patient-${user.id}`}
                                      checked={editFormData.is_patient || false}
                                      onChange={(e) => updateEditFormData('is_patient', e.target.checked)}
                                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                  ) : (
                                    <div className={`h-3 w-3 rounded border-2 ${user.is_patient ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {user.is_patient && <div className="text-white text-xs">✓</div>}
                                    </div>
                                  )}
                                  <label htmlFor={`patient-${user.id}`} className="ml-2 block text-xs text-gray-900">
                                    Patient
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  {editingSection === 'security-settings' && editingUserId === user.id ? (
                                    <input
                                      type="checkbox"
                                      id={`clinician-${user.id}`}
                                      checked={editFormData.is_clinician || false}
                                      onChange={(e) => updateEditFormData('is_clinician', e.target.checked)}
                                      className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                  ) : (
                                    <div className={`h-3 w-3 rounded border-2 ${user.is_clinician ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {user.is_clinician && <div className="text-white text-xs">✓</div>}
                                    </div>
                                  )}
                                  <label htmlFor={`clinician-${user.id}`} className="ml-2 block text-xs text-gray-900">
                                    Clinician
                                  </label>
                                </div>
                              </div>

                              {/* Patient Type Dropdown - Show when editing patient users */}
                              {editingSection === 'security-settings' && editingUserId === user.id && editFormData.is_patient && (
                                <div className="mt-3">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Patient Type
                                  </label>
                                  <select
                                    value={editFormData.patient_type || 'Standard'}
                                    onChange={(e) => updateEditFormData('patient_type', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value="Standard">Standard Patient</option>
                                    <option value="Aged Care">Aged Care Patient</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                  {filteredUsers.length !== usersData.length && (
                    <span className="text-blue-600 ml-1">(filtered from {usersData.length} total)</span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={addUserData.first_name}
                    onChange={(e) => updateAddUserData('first_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="First Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={addUserData.last_name}
                    onChange={(e) => updateAddUserData('last_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email * (will be used as username)
                </label>
                <input
                  type="email"
                  value={addUserData.email}
                  onChange={(e) => updateAddUserData('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={addUserData.date_of_birth}
                  onChange={(e) => updateAddUserData('date_of_birth', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={addUserData.password}
                  onChange={(e) => updateAddUserData('password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Leave empty to auto-generate"
                />
              </div>

              {/* Address Information */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-2">Address Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={addUserData.user_address}
                      onChange={(e) => updateAddUserData('user_address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Street Address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Suburb
                      </label>
                      <input
                        type="text"
                        value={addUserData.suburb}
                        onChange={(e) => updateAddUserData('suburb', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Suburb"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={addUserData.state}
                        onChange={(e) => updateAddUserData('state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="State"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postcode
                      </label>
                      <input
                        type="text"
                        value={addUserData.postcode}
                        onChange={(e) => updateAddUserData('postcode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Postcode"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={addUserData.country}
                        onChange={(e) => updateAddUserData('country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={addUserData.phone_number}
                  onChange={(e) => updateAddUserData('phone_number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Phone Number"
                />
              </div>

              {/* User Roles */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-2">User Roles</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={addUserData.is_active}
                      onChange={(e) => updateAddUserData('is_active', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_superuser"
                      checked={addUserData.is_superuser}
                      onChange={(e) => updateAddUserData('is_superuser', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_superuser" className="ml-2 block text-sm text-gray-900">
                      Superuser
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_doctor"
                      checked={addUserData.is_doctor}
                      onChange={(e) => updateAddUserData('is_doctor', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_doctor" className="ml-2 block text-sm text-gray-900">
                      Doctor
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_patient"
                      checked={addUserData.is_patient}
                      onChange={(e) => updateAddUserData('is_patient', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_patient" className="ml-2 block text-sm text-gray-900">
                      Patient
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_clinician"
                      checked={addUserData.is_clinician}
                      onChange={(e) => updateAddUserData('is_clinician', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_clinician" className="ml-2 block text-sm text-gray-900">
                      Clinician
                    </label>
                  </div>
                </div>
              </div>

              {/* Patient Type Dropdown - Show only when patient role is selected */}
              {addUserData.is_patient && (
                <div className="mb-4">
                  <label htmlFor="patient_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Patient Type
                  </label>
                  <select
                    id="patient_type"
                    value={addUserData.patient_type}
                    onChange={(e) => updateAddUserData('patient_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Standard">Standard Patient</option>
                    <option value="Aged Care">Aged Care Patient</option>
                  </select>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={closeAddModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                disabled={addLoading || !addUserData.first_name || !addUserData.last_name || !addUserData.email}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2" style={{ maxWidth: '400px', width: '100%' }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 ${
              toast.type === 'error' ? 'border-l-4 border-red-500' : 'border-l-4 border-green-500'
            }`}
            style={{ minWidth: '320px' }}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {toast.type === 'error' ? (
                    <X className="h-5 w-5 text-red-400" />
                  ) : (
                    <Plus className="h-5 w-5 text-green-400" />
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900 break-words">{toast.message}</p>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => removeToast(toast.id)}
                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminUsersPage;

